import type { Message } from '../../api/types'
import type { SheetMenuItem } from '../../components/ActionSheet'
import { copyText } from '../../lib/platform'
import { toast } from '../../state/toasts'
import { canFetchAttachment, canShareFiles, filesNoun, saveAttachments, shareAttachments } from './attachmentFiles'
import { useConversation } from './ConversationContext'
import { pinMessage, unpinMessage } from './messageActions'
import type { MessageRules } from './messageRules'
import { usePrivateReply } from './privateReply'

/** The actions in the message sheet, in order, each where the rules allow it. */
export function useMessageMenu(message: Message, rules: MessageRules, onEdit: () => void): SheetMenuItem[] {
  const { channel, me, startReply, requestForward, requestDelete } = useConversation()
  const replyPrivately = usePrivateReply()
  // Someone else's clip can't be fetched while it's processing, or once it has failed.
  const files = message.attachments.filter((a) => canFetchAttachment(a, message.authorId === me.id))
  const items: SheetMenuItem[] = []

  if (rules.reply) items.push({ id: 'reply', label: 'Reply', icon: 'reply', run: () => startReply(message) })
  if (rules.replyBot) {
    items.push({ id: 'reply-privately', label: 'Reply privately', icon: 'reply', run: () => void replyPrivately(channel.id, message.id) })
  }
  if (rules.forward) items.push({ id: 'forward', label: 'Forward', icon: 'forward', run: () => requestForward(message) })
  if (rules.copy) {
    items.push({
      id: 'copy',
      label: 'Copy text',
      icon: 'copy',
      run: () => void copyText(message.body).then((copied) => toast(copied ? 'Copied to clipboard' : "Couldn't copy the text")),
    })
  }
  if (files.length) {
    items.push({ id: 'save', label: `Save ${filesNoun(files)}`, icon: 'download', run: () => void saveAttachments(files) })
    if (canShareFiles(files)) items.push({ id: 'share', label: `Share ${filesNoun(files)}`, icon: 'share', run: () => void shareAttachments(files) })
  }
  if (rules.edit) items.push({ id: 'edit', label: 'Edit', icon: 'edit', run: onEdit })
  if (rules.pin) {
    items.push({
      id: 'pin',
      label: rules.pinned ? 'Unpin' : 'Pin',
      icon: 'pin',
      run: () => void (rules.pinned ? unpinMessage(message.id) : pinMessage(message.id)),
    })
  }
  if (rules.delete) items.push({ id: 'delete', label: 'Delete', icon: 'trash', danger: true, run: () => requestDelete(message) })
  return items
}
